import { Link } from 'react-router-dom';
import type { LiveSession } from '@/mocks/lives';
import { formatCount, statusText, toNextTimestamp } from '@/utils/time';
import CountdownTimer from '@/components/feature/CountdownTimer';
import ReminderButton from '@/components/feature/ReminderButton';

const statusStyle: Record<string, string> = {
  live: 'bg-accent-500 text-background-50',
  upcoming: 'bg-background-50/90 text-foreground-900',
  ended: 'bg-foreground-950/70 text-background-100',
};

export default function LiveCard({ session }: { session: LiveSession }) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors duration-200 hover:border-primary-300">
      <Link to={`/live/${session.id}`} className="relative block w-full" aria-label={session.title}>
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-background-200">
          <img
            src={session.cover}
            alt={`爆料直播网 - ${session.title} 直播封面`}
            title={`${session.title} 爆料直播 今日直播排期`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/55 via-transparent to-transparent" />
          <span
            className={`absolute left-3 top-3 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium backdrop-blur-sm ${statusStyle[session.status]}`}
          >
            {session.status === 'live' && (
              <span className="h-1.5 w-1.5 rounded-full bg-background-50 animate-pulse" />
            )}
            {statusText[session.status]}
          </span>
          {session.status !== 'upcoming' && session.viewers > 0 && (
            <span className="absolute bottom-3 left-3 flex items-center gap-1.5 text-[11px] text-background-100">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-eye-line" aria-hidden="true" />
              </span>
              {formatCount(session.viewers)} 人在看
            </span>
          )}
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900">
            {session.category}
          </span>
          <span className="text-foreground-500">
            {session.start} - {session.end}
          </span>
        </div>

        <h3 className="mt-2.5">
          <Link
            to={`/live/${session.id}`}
            className="font-heading text-[15px] font-bold leading-snug text-foreground-950 transition-colors hover:text-primary-700 md:text-base"
          >
            {session.title}
          </Link>
        </h3>

        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{session.summary}</p>

        <div className="mt-4 flex items-center gap-2 text-xs text-foreground-500">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-[11px] font-semibold text-primary-800">
            {session.host.slice(0, 1)}
          </span>
          主播 {session.host}
          <span className="text-foreground-300">·</span>
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-fire-line" aria-hidden="true" />
            </span>
            热度 {formatCount(session.hot)}
          </span>
        </div>

        <div className="mt-auto flex items-center justify-between gap-3 border-t border-background-200 pt-3.5">
          {session.status === 'upcoming' ? (
            <CountdownTimer targetMs={toNextTimestamp(session.start)} />
          ) : (
            <span
              className={`text-xs font-medium ${
                session.status === 'live' ? 'text-accent-600' : 'text-foreground-400'
              }`}
            >
              {session.status === 'live' ? '直播进行中' : '回放可补看'}
            </span>
          )}
          {session.status !== 'ended' && <ReminderButton sessionId={session.id} />}
        </div>
      </div>
    </article>
  );
}